/**
 * [rev16 Phase A] ItemsDetailSection — read-only line-items block.
 *
 * Read-only presentation of `Transaction.items` for the Detail modal. Pure
 * presentation — no mutation, no callbacks.
 *
 * [R27-D] Replaced local gross / discount / net math with the shared
 * `buildTransactionBreakdown` helper from `@/lib/transactionBreakdown`,
 * rendered via the shared `ReceiptItemRow` row primitive and the
 * `FinalizedBreakdownSummary` summary leaf. Local formulas are gone; this
 * component only consumes finalized breakdown data passed in by the parent.
 *
 * Numbers follow the helper's rules (per-row net = max(0, amount × quantity −
 * discount), aggregate itemNet = sum of complete rows, allocationState
 * honest). The formatting is delegated to the caller's `fmt` so a single
 * `Intl.NumberFormat` instance is reused.
 *
 * Mobile-first: no horizontal scrolling, long descriptions wrap with
 * `break-words`, every visual region fits inside a 320px viewport.
 *
 * Loading / empty / error / partial-data states are honored. Loading and
 * error render a brief message under the canonical `data-testid` host; the
 * empty state keeps the original "No line items." text. The
 * `FinalizedBreakdownSummary` only mounts when at least one item is
 * present, so no fabricated zero-allocation totals are produced.
 *
 * Stable test IDs:
 *   - `data-testid="items-detail-empty"` — the empty-state block.
 *   - `data-testid="items-detail-section"` — the open `<details>` host.
 *   - `data-testid="items-detail-list"` — the `<ul>` of items.
 *   - `data-testid="items-detail-row-{i}"` — one `<li>` per item (0-indexed).
 *   - `data-testid="items-detail-net"` — the aggregate net total.
 *   - `finalized-breakdown-summary` + `finalized-breakdown-{transaction,item}-{gross,discount,net}`
 *     + `finalized-breakdown-allocation` — surfaced by the shared summary leaf.
 *
 * @see docs/superpowers/plans/2026-08-16-rev16-modal-redesign-plan.md (Section 4.2)
 * @ticket R27-D
 */
import * as React from 'react';

import type { Category, TransactionItem } from '@/lib/types';
import ReceiptItemRow from '@/components/transactions/ReceiptItemRow';
import { FinalizedBreakdownSummary } from '@/components/transactions/FinalizedBreakdownSummary';
import type { TransactionBreakdown } from '@/lib/transactionBreakdown';

export interface ItemsDetailSectionProps {
  /** The line items attached to the current transaction. */
  items: ReadonlyArray<TransactionItem>;
  /** Finalized breakdown computed by `buildTransactionBreakdown`. */
  breakdown: TransactionBreakdown;
  /** Currency/formatting helper invoked as `fmt(value)`. */
  fmt: (n: number) => string;
  /** When true, renders a loading placeholder inside the host. */
  loading?: boolean;
  /** When set, renders the error text inside the host. */
  error?: string | null;
  categories?: ReadonlyArray<Category>;
  transactionCategoryId?: string;
  transactionCategoryName?: string;
  transactionCategoryEmoji?: string;
}

export function ItemsDetailSection(props: ItemsDetailSectionProps): React.ReactElement {
  const { items, breakdown, fmt, loading, error, categories = [], transactionCategoryId, transactionCategoryName, transactionCategoryEmoji } = props;

  // Aggregate net total from the finalized breakdown. Fall back to the
  // transaction-level net only when item-level totals are unavailable so we
  // never fabricate a zero allocation.
  const headerNet = breakdown.itemNet;
  const headerDisplay = headerNet === null ? 'Unavailable' : fmt(headerNet);
  const listDisplay = headerNet === null ? 'Unavailable' : fmt(headerNet);

  // State-aware body. The open <details> host (data-testid="items-detail-section")
  // is preserved across every state so the rev16 selector contract holds for
  // populated, loading, error, and empty cases — matching the original Detail's
  // <details><div data-testid="items-detail-empty">…</div></details> pattern.
  let body: React.ReactNode;
  if (loading) {
    body = (
      <div
        data-testid="items-detail-loading"
        className="px-3 py-2 text-sm text-muted-foreground"
        role="status"
        aria-live="polite"
      >
        Loading line items…
      </div>
    );
  } else if (error) {
    body = (
      <div
        data-testid="items-detail-error"
        className="px-3 py-2 text-sm text-destructive break-words"
        role="alert"
      >
        {error}
      </div>
    );
  } else if (items.length === 0) {
    body = (
      <div
        data-testid="items-detail-empty"
        className="px-3 py-2 text-sm text-muted-foreground"
      >
        No line items.
      </div>
    );
  } else {
    body = (
      <>
        <summary className="flex cursor-pointer items-center justify-between gap-3 text-sm font-medium text-foreground/80 hover:text-foreground list-none [&::-webkit-details-marker]:hidden">
          <span className="min-w-0 break-words">
            <span aria-hidden="true" className="mr-1">▾</span>
            Line Items ({items.length})
          </span>
          <span
            className="text-xs text-muted-foreground tabular-nums shrink-0"
            aria-label={`Net total ${headerDisplay}`}
          >
            {headerDisplay}
          </span>
        </summary>
        <ul
          data-testid="items-detail-list"
          className="mt-3 space-y-2.5 pl-0 sm:pl-1"
        >
          {items.map((item, index) => (
            <li
              key={`items-detail-row-${index}`}
              data-testid={`items-detail-row-${index}`}
            >
              <ReceiptItemRow
                item={(() => {
                  const category = categories.find((entry) => entry.id === item.categoryId);
                  return {
                    ...item,
                    categoryName: item.categoryName
                      ?? category?.name
                      ?? (item.category && item.category !== transactionCategoryId ? item.category : undefined)
                      ?? (item.categoryId && item.categoryId !== transactionCategoryId ? `Category ${item.categoryId}` : transactionCategoryName),
                    categoryEmoji: item.categoryEmoji ?? category?.emoji ?? (!item.categoryId ? transactionCategoryEmoji : undefined),
                    categoryColor: item.categoryColor ?? category?.color,
                  };
                })()}
                rowNumber={index + 1}
                density="detailed"
                transactionCategory={transactionCategoryId}
                className="py-1.5 text-xs"
              />
            </li>
          ))}
        </ul>
        <span
          data-testid="items-detail-net"
          className="sr-only"
          aria-label={`Net total ${listDisplay}`}
        >
          {listDisplay}
        </span>
        <div className="mt-4">
          <FinalizedBreakdownSummary breakdown={breakdown} fmt={fmt} />
        </div>
      </>
    );
  }

  return (
    <details
      open
      data-testid="items-detail-section"
      className="border-b border-border py-4"
    >
      {body}
    </details>
  );
}


export default ItemsDetailSection;
